/**
 * Typography utilities for preventing orphaned words and improving text layout
 */

/**
 * Prevents orphaned words by adding non-breaking spaces
 * between the last 2-3 words of a text string
 */
export function preventOrphans(text: string, minWords: number = 2): string {
  if (!text || typeof text !== 'string') return text;
  
  // Remove any existing &nbsp; entities first
  let cleanText = text.replace(/&nbsp;/g, ' ').trim();
  
  // Split into words
  const words = cleanText.split(' ').filter(word => word.length > 0);
  
  // Only process if we have more than minWords
  if (words.length <= minWords) return cleanText;
  
  // Determine how many words to keep together based on last word length
  const lastWord = words[words.length - 1];
  const secondLastWord = words[words.length - 2];
  const thirdLastWord = words[words.length - 3] || '';
  
  let wordsToJoin = 2; // Default
  
  // If last word is very short (<=3 chars), try to keep 3 words together
  if (lastWord.length <= 3 && words.length >= 3) {
    wordsToJoin = 3;
  }
  // If second-to-last word is also short, keep 3 together
  else if (secondLastWord.length <= 4 && words.length >= 3) {
    wordsToJoin = 3;
  }
  // If we have a very long last word (>10 chars), it's probably OK alone
  else if (lastWord.length > 10) {
    return cleanText; // Don't modify
  }
  
  // Rebuild the string with non-breaking spaces
  if (wordsToJoin === 3 && words.length >= 3) {
    const beforeLast = words.slice(0, -3).join(' ');
    const lastThree = words.slice(-3).join('\u00A0'); // Unicode non-breaking space
    return beforeLast ? `${beforeLast} ${lastThree}` : lastThree;
  } else {
    const beforeLast = words.slice(0, -2).join(' ');
    const lastTwo = words.slice(-2).join('\u00A0');
    return beforeLast ? `${beforeLast} ${lastTwo}` : lastTwo;
  }
}

/**
 * Smart text balancing for headlines
 * Tries to create more balanced line breaks in multi-line text
 */
export function balanceHeadline(text: string): string {
  if (!text || typeof text !== 'string') return text;
  
  const words = text.split(' ').filter(word => word.length > 0);
  
  // For short headlines (< 5 words), keep all together
  if (words.length < 5) {
    return words.join('\u00A0');
  }
  
  // For medium headlines (5-8 words), try to balance into 2 lines
  if (words.length >= 5 && words.length <= 8) {
    const midPoint = Math.ceil(words.length / 2);
    const firstHalf = words.slice(0, midPoint).join(' ');
    const secondHalf = words.slice(midPoint).join('\u00A0'); // Keep second half together
    return `${firstHalf} ${secondHalf}`;
  }
  
  // For longer headlines, just prevent orphans
  return preventOrphans(text);
}

/**
 * Adds soft hyphens for better word breaking in narrow containers
 * Useful for long words that might overflow
 */
export function addSoftHyphens(text: string, minWordLength: number = 10): string {
  if (!text || typeof text !== 'string') return text;
  
  // Common hyphenation points for compound words
  const hyphenationPatterns = [
    { pattern: /([a-z])([A-Z])/g, replacement: '$1\u00AD$2' }, // camelCase
    { pattern: /(mortgage)([a-z])/gi, replacement: '$1\u00AD$2' }, // mortgage-*
    { pattern: /(lead)([a-z])/gi, replacement: '$1\u00AD$2' }, // lead-*
    { pattern: /(market)(ing)/gi, replacement: '$1\u00AD$2' }, // market-ing
    { pattern: /(generat)(ion|ing|ed)/gi, replacement: '$1\u00AD$2' }, // generat-ion
    { pattern: /(refinanc)(e|ing|ed)/gi, replacement: '$1\u00AD$2' }, // refinanc-e
    { pattern: /(profession)(al|als)/gi, replacement: '$1\u00AD$2' }, // profession-al
  ];
  
  let result = text;
  
  // Apply hyphenation patterns
  hyphenationPatterns.forEach(({ pattern, replacement }) => {
    result = result.replace(pattern, replacement);
  });
  
  // For very long words without patterns, add soft hyphens every 8-10 chars
  result = result.replace(/\b(\w{10,})\b/g, (match) => {
    if (!match.includes('\u00AD')) {
      // Add soft hyphens approximately every 8 characters
      return match.replace(/(.{8})(?=.{4,})/g, '$1\u00AD');
    }
    return match;
  });
  
  return result;
}

/**
 * React component wrapper for text processing
 */
export function processText(
  text: string,
  options: {
    preventOrphans?: boolean;
    balanceHeadlines?: boolean;
    addSoftHyphens?: boolean;
    isHeadline?: boolean;
  } = {}
): string {
  const {
    preventOrphans: shouldPreventOrphans = true,
    balanceHeadlines = false,
    addSoftHyphens: shouldAddSoftHyphens = false,
    isHeadline = false
  } = options;
  
  let processed = text;
  
  // Add soft hyphens first (if needed)
  if (shouldAddSoftHyphens) {
    processed = addSoftHyphens(processed);
  }
  
  // Balance headlines or prevent orphans
  if (isHeadline && balanceHeadlines) {
    processed = balanceHeadline(processed);
  } else if (shouldPreventOrphans) {
    processed = preventOrphans(processed);
  }
  
  return processed;
}

/**
 * React hook for processing text
 */
export function useProcessedText(
  text: string,
  options?: Parameters<typeof processText>[1]
): string {
  return processText(text, options);
}